/* eslint-disable @typescript-eslint/no-explicit-any */
import Image from "next/image";
import { formatNumber } from "src/helpers/api/sharedFunction";
import React, { useEffect, useState } from "react";
import DownTrade from "src/assets/images/DownTrade.svg";
import List from "src/assets/images/List.svg";
import PledgeInvoke from "src/assets/images/PledgeInvoke.svg";
import PledgeRevoke from "src/assets/images/PledgeRevoke.svg";
import UpTrade from "src/assets/images/UpTrade.svg";
import { InsiderSummaryData } from "src/helpers";

interface InsiderStatsProps {
  insiderTradingSummary: InsiderSummaryData | null;
}

const iconMapping: Record<string, string> = {
  Buy: UpTrade,
  Sell: DownTrade,
  Pledge: PledgeInvoke,
  "Pledge Invoke": PledgeInvoke,
  "Pledge Revoke": PledgeRevoke,
  "Trades Reported": List,
};

const displayOrder = ["Trades Reported", "Buy", "Sell"];

export const colorMapping: Record<string, { text: string; bg: string }> = {
  Buy: {
    text: "text-green-600 dark:text-green-400",
    bg: "bg-green-100 dark:bg-green-900",
  },
  Sell: {
    text: "text-red-600 dark:text-red-400",
    bg: "bg-red-100 dark:bg-red-900",
  },
  "Pledge Invoke": {
    text: "text-blue-600 dark:text-blue-400",
    bg: "bg-blue-100 dark:bg-blue-900",
  },
  "Pledge Revoke": {
    text: "text-orange-500 dark:text-orange-400",
    bg: "bg-orange-100 dark:bg-orange-900",
  },
  Pledge: {
    text: "text-yellow-600 dark:text-yellow-400",
    bg: "bg-yellow-100 dark:bg-yellow-900",
  },
  "Trades Reported": {
    text: "text-gray-700 dark:text-gray-300",
    bg: "bg-gray-200 dark:bg-gray-700",
  },
};

const InsiderStateCard = ({ insiderTradingSummary }: InsiderStatsProps) => {
  const [stats, setStats] = useState<
    {
      title: string;
      value: string | number;
      icon: string;
      color: string;
      bgColor: string;
    }[]
  >([]);

  useEffect(() => {
    if (!insiderTradingSummary) return;

    const entries = Object.entries(insiderTradingSummary);
    const sortedEntries = entries.sort(([keyA], [keyB]) => {
      const indexA = displayOrder.indexOf(keyA);
      const indexB = displayOrder.indexOf(keyB);

      if (indexA === -1 && indexB === -1) return keyA.localeCompare(keyB);
      if (indexA === -1) return 1;
      if (indexB === -1) return -1;
      return indexA - indexB;
    });

    const formattedStats = sortedEntries.map(([key, value]) => {
      const colors = colorMapping[key] || {
        text: "text-[#00A85F] dark:text-green-400",
        bg: "bg-[#baf3d2] dark:bg-[#394564]",
      };

      return {
        title: key,
        value: formatNumber(value, key === "Trades Reported"),
        icon: iconMapping[key] || List,
        color: colors.text,
        bgColor: colors.bg,
      };
    });

    // eslint-disable-next-line react-hooks/set-state-in-effect
    setStats(formattedStats);
  }, [insiderTradingSummary]);

  return (
    <>
      {/* Mobile Layout */}
      <div className="block lg:hidden">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 p-4">
          {stats.map((stat, index) => (
            <div
              key={index}
              className="flex sm:flex-row items-start sm:items-center gap-3 sm:gap-2 space-y-2 sm:space-y-0 sm:space-x-3 bg-gray-100 dark:bg-gray-800/50 rounded-lg p-3 sm:p-4 lg:p-5 border border-gray-200 dark:border-gray-700/50"
            >
              <div
                className={`p-[5px] rounded-full ${stat.bgColor} flex-shrink-0`}
              >
                <Image
                  src={stat.icon}
                  alt={stat.title}
                  className="w-4 h-4 object-contain filter dark:invert-0"
                />
              </div>

              <div className="min-w-0 flex items-center gap-2">
                <div>
                  <p className="text-sm leading-5 font-medium text-[#3D3D3D] dark:text-white font-figtree truncate">
                    {stat.title}
                  </p>
                  {Array.isArray(stat.value) ? (
                    <div className="max-h-[80px] overflow-y-auto custom-scrollbar mt-1 pr-1">
                      {stat.value.map(
                        (item: any, i: React.Key | null | undefined) => (
                          <p
                            key={i}
                            className={`text-sm font-figtree ${stat.color}`}
                          >
                            {item.label} : {item.amount}
                          </p>
                        ),
                      )}
                    </div>
                  ) : (
                    <p
                      className={`text-xl font-medium font-figtree ${stat.color}`}
                    >
                      {stat.value}
                    </p>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Desktop Layout */}
      <div className="hidden lg:flex justify-between items-center border border-[#d0d4e4] dark:border-[#394564] rounded-md bg-white my-0 !mt-0 dark:bg-[#121419] w-full shadow-[5px_1px_5px_rgba(0,0,0,0.09)]">
        <div className="flex items-center justify-between w-full py-4">
          {stats.map((stat, index) => {
            const isLast = index === stats.length - 1;
            return (
              <div
                key={index}
                className={`
                  flex items-start space-x-3 flex-1 px-4 py-0 justify-start
                  ${!isLast ? "border-r border-[#d0d4e4] dark:border-gray-700" : ""}
                `}
              >
                <div
                  className={`p-[5px] rounded-full ${stat.bgColor} flex-shrink-0`}
                >
                  <Image
                    src={stat.icon}
                    alt={stat.title}
                    className="w-4 h-4 object-contain filter invert-0 dark:invert-100"
                  />
                </div>

                <div className="flex items-center gap-2">
                  <div>
                    <p
                      className={`text-sm leading-5 font-medium ${stat.color} min-w-[140px] font-figtree whitespace-nowrap`}
                    >
                      {stat.title}
                    </p>
                    {Array.isArray(stat.value) ? (
                      <div className="h-[40px] overflow-y-auto mt-1 pr-1">
                        {stat.value.map(
                          (item: any, i: React.Key | null | undefined) => (
                            <p
                              key={i}
                              className={`text-sm font-figtree leading-tight ${stat.color}`}
                            >
                              {item.label} : {item.amount}
                            </p>
                          ),
                        )}
                      </div>
                    ) : (
                      <p
                        className={`w-[150px] text-xl font-medium font-figtree leading-tight truncate ${stat.color}`}
                      >
                        {stat.value}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
};

export default React.memo(InsiderStateCard);
